import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, CheckCircle, Loader2, AlertCircle, Users, Calendar, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import api from '../utils/api';

const SelfCheckIn = () => {
  const navigate = useNavigate();
  
  const [bookingId, setBookingId] = useState('');
  const [lastName, setLastName] = useState('');
  const [searching, setSearching] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [booking, setBooking] = useState(null);
  const [checkedIn, setCheckedIn] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!bookingId.trim() || !lastName.trim()) {
      toast.error('Please enter both Booking ID and Last Name');
      return;
    }

    setSearching(true);
    try {
      const response = await api.post('/public/self-check-in/lookup', {
        confirmationCode: bookingId.trim().toUpperCase(),
        lastName: lastName.trim()
      });
      
      if (response.data.found) {
        setBooking(response.data.booking);
        toast.success('Booking found!');
      } else {
        toast.error('Booking not found. Please check your details.');
        setBooking(null);
      }
    } catch (error) {
      console.error('Lookup error:', error);
      const errorMsg = error.response?.data?.detail || 'Booking not found';
      toast.error(errorMsg);
      setBooking(null);
    } finally {
      setSearching(false);
    }
  };

  const handleCheckIn = async () => {
    setCheckingIn(true);
    try {
      const response = await api.post(`/public/self-check-in/${booking.id}`);
      
      if (response.data.alreadyCheckedIn) {
        toast.info('You are already checked in!');
      } else {
        toast.success('✅ Checked in successfully!');
      }
      
      setCheckedIn(true);
      setBooking({ ...booking, checkInStatus: 'checked_in', checkInAt: response.data.checkInAt });
    } catch (error) {
      console.error('Check-in error:', error);
      toast.error(error.response?.data?.detail || 'Check-in failed');
    } finally {
      setCheckingIn(false);
    }
  };

  const resetForm = () => {
    setBookingId('');
    setLastName('');
    setBooking(null);
    setCheckedIn(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
            <CheckCircle className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Self Check-In</h1>
          <p className="text-gray-600">Ready for your booking? Check in now</p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          {!booking && !checkedIn && (
            <form onSubmit={handleSearch} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Booking ID <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={bookingId}
                  onChange={(e) => setBookingId(e.target.value.toUpperCase())}
                  placeholder="e.g., BF-ABC123"
                  className="h-12 text-base font-mono"
                  disabled={searching}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Enter your last name"
                  className="h-12 text-base"
                  disabled={searching}
                />
              </div>

              <Button
                type="submit"
                disabled={searching || !bookingId.trim() || !lastName.trim()}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-base font-semibold"
              >
                {searching ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5 mr-2" />
                    Find My Booking
                  </>
                )}
              </Button>
            </form>
          )}

          {booking && !checkedIn && (
            <div className="space-y-6">
              <div className="text-center pb-4 border-b">
                <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
                <h2 className="text-xl font-bold text-gray-900">Booking Found!</h2>
              </div>

              <div className="space-y-3">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Customer</p>
                  <p className="text-lg font-bold text-gray-900">{booking.customerName}</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Service</p>
                  <p className="text-base font-semibold text-gray-900">{booking.serviceName}</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Date</p>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-600" />
                      <p className="text-sm font-semibold text-gray-900">
                        {booking.preferredDate || booking.date || 'N/A'}
                      </p>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Time</p>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-600" />
                      <p className="text-sm font-semibold text-gray-900">
                        {booking.preferredTime || booking.startTime || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {booking.partySize && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Party Size</p>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-600" />
                      <p className="text-base font-semibold text-gray-900">{booking.partySize} guests</p>
                    </div>
                  </div>
                )}

                <div className={`rounded-lg p-4 ${
                  booking.checkInStatus === 'checked_in' 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-yellow-50 border border-yellow-200'
                }`}>
                  <p className="text-xs uppercase tracking-wide mb-1 ${
                    booking.checkInStatus === 'checked_in' ? 'text-green-700' : 'text-yellow-700'
                  }">Status</p>
                  <p className={`text-base font-bold ${
                    booking.checkInStatus === 'checked_in' ? 'text-green-900' : 'text-yellow-900'
                  }`}>
                    {booking.checkInStatus === 'checked_in' ? '✅ Already Checked In' : '⏳ Not Checked In'}
                  </p>
                </div>
              </div>

              {booking.checkInStatus !== 'checked_in' ? (
                <Button
                  onClick={handleCheckIn}
                  disabled={checkingIn}
                  className="w-full h-14 bg-green-600 hover:bg-green-700 text-lg font-bold"
                >
                  {checkingIn ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Checking In...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Check In Now
                    </>
                  )}
                </Button>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-green-900 font-semibold">You're already checked in! See you soon 👋</p>
                </div>
              )}

              <Button
                onClick={resetForm}
                variant="outline"
                className="w-full"
              >
                Back to Search
              </Button>
            </div>
          )}

          {checkedIn && (
            <div className="text-center space-y-6">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">You're Checked In! 🎉</h2>
                <p className="text-lg font-semibold text-gray-700">{booking.customerName}</p>
                <p className="text-sm text-gray-600 mt-2">
                  Checked in at {new Date(booking.checkInAt).toLocaleTimeString()}
                </p>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <p className="text-green-900 font-medium">See you soon! 👋</p>
              </div>

              <Button
                onClick={resetForm}
                variant="outline"
                className="w-full"
              >
                Check In Another Booking
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SelfCheckIn;
